/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useLayoutEffect, useState } from "react";

import { createPortal } from "react-dom";

import { tutorialPulse } from "@/shared";

import { colors } from "@dotnet/telematics-design-system";

import { TutorialStep } from "./types";

interface TutorialPortalProps {
  tutorialHighlightElement: TutorialStep | null;
}

const TutorialPortal = ({ tutorialHighlightElement }: TutorialPortalProps) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  const targetId = tutorialHighlightElement?.highlightId ?? null;
  const spotlightPadding = tutorialHighlightElement?.spotlightPadding ?? null;

  useLayoutEffect(() => {
    if (!targetId) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const element = document.getElementById(targetId);
      setRect(element ? element.getBoundingClientRect() : null);
    };

    const observer = new MutationObserver(updateRect);

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(updateRect, 300);
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [targetId]);

  return createPortal(
    <div
      css={css({
        position: "fixed",
        top: rect?.top ?? 0,
        left: rect?.left ?? 0,
        width: rect?.width ?? 0,
        height: rect?.height ?? 0,
        outline: `3px solid ${colors.blue700}`,
        borderRadius: spotlightPadding ? "8px" : "4px",
        pointerEvents: "none",
        zIndex: 10001,
        overscrollBehavior: "none",
        boxShadow: "0 0 0 0 rgba(166,200,255,0.7)",
        animation: `${tutorialPulse} 2s linear infinite`,
        visibility: rect ? "visible" : "hidden",

        ...(spotlightPadding
          ? { outlineOffset: `${Number(spotlightPadding)}px` }
          : {})
      })}
      onWheel={e => e.stopPropagation()}
    />,
    document.body
  );
};

export default TutorialPortal;
